import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isNotFoundError } from '../../util/errors';
import { userInitialsAsString } from '../../util/data';

import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import {
  AvatarLarge,
  CollectionPieceCard,
  CollectorCard,
  H2,
  LayoutSingleColumn,
  NamedLink,
  Page,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import CollectionPieceModal from './CollectionPieceModal';
import { clearAskState } from './CollectionPage.duck';

import flourishImage from '../../assets/nextmine-flourish-gold.svg';
import css from './CollectionPage.module.css';

/**
 * Nextmine: en samlers egen side.
 *
 * "Share my collection" handler om at samlere kan vise frem, hvad de EJER -
 * ikke hvad de sælger. Siden har derfor sin egen adresse (/collections/:id),
 * som de kan dele, og den ligner en udstilling frem for en butik.
 */
export const CollectionPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [openPieceId, setOpenPieceId] = useState(null);

  const {
    scrollingDisabled,
    collector,
    pieces,
    showCollectorError,
    queryCollectionError,
    currentUser,
    isAuthenticated,
    onManageDisableScrolling,
    onClearAskState,
    params,
  } = props;

  if (isNotFoundError(showCollectorError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const profile = collector?.attributes?.profile;
  const collectorPublicData = profile?.publicData || {};
  // Nextmine: samleren vises kun med initialer.
  const displayName = userInitialsAsString(collector, '');
  const bio = profile?.bio;
  const isOwnCollection = !!currentUser?.id && currentUser.id.uuid === params.id;
  const hasPieces = pieces.length > 0;

  const openPiece = pieces.find(p => p.id.uuid === openPieceId) || null;

  const title = displayName
    ? intl.formatMessage({ id: 'CollectionPage.schemaTitle' }, { name: displayName })
    : intl.formatMessage({ id: 'CollectionPage.schemaTitleFallback' });

  const handleClosePiece = () => {
    setOpenPieceId(null);
    onClearAskState();
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={title}
      description={intl.formatMessage(
        { id: 'CollectionPage.schemaDescription' },
        { name: displayName, marketplaceName: config.marketplaceName }
      )}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'CollectionPage',
        name: title,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <header className={css.header}>
            <AvatarLarge className={css.avatar} user={collector} disableProfileLink />
            <H2 as="h1" className={css.heading}>
              <FormattedMessage id="CollectionPage.heading" values={{ name: displayName }} />
            </H2>
            <p className={css.tagline}>
              <FormattedMessage id="CollectionPage.tagline" />
            </p>
            {bio ? <p className={css.bio}>{bio}</p> : null}
            <CollectorCard className={css.collectorCard} publicData={collectorPublicData} />
            <div className={css.headerLinks}>
              <NamedLink className={css.headerLink} name="ProfilePage" params={{ id: params.id }}>
                <FormattedMessage id="CollectionPage.viewProfile" />
              </NamedLink>
              {isOwnCollection ? (
                <NamedLink className={css.headerLink} name="MyCollectionPage">
                  <FormattedMessage id="CollectionPage.editCollection" />
                </NamedLink>
              ) : null}
            </div>
          </header>

          {queryCollectionError ? (
            <p className={css.error}>
              <FormattedMessage id="CollectionPage.loadingFailed" />
            </p>
          ) : hasPieces ? (
            <ul className={css.grid}>
              {pieces.map(piece => (
                <li className={css.gridItem} key={piece.id.uuid}>
                  <CollectionPieceCard piece={piece} onClick={p => setOpenPieceId(p.id.uuid)} />
                </li>
              ))}
            </ul>
          ) : (
            <div className={css.empty}>
              <p className={css.emptyNote}>
                {isOwnCollection ? (
                  <FormattedMessage id="CollectionPage.emptyOwn" />
                ) : (
                  <FormattedMessage id="CollectionPage.empty" values={{ name: displayName }} />
                )}
              </p>
              {isOwnCollection ? (
                <NamedLink className={css.headerLink} name="MyCollectionPage">
                  <FormattedMessage id="CollectionPage.startCollection" />
                </NamedLink>
              ) : null}
            </div>
          )}

          <img src={flourishImage} alt="" className={css.flourish} />
        </div>
      </LayoutSingleColumn>

      <CollectionPieceModal
        piece={openPiece}
        isOpen={!!openPiece}
        onClose={handleClosePiece}
        onManageDisableScrolling={onManageDisableScrolling}
        collectorName={displayName}
        isOwnPiece={isOwnCollection}
        isAuthenticated={isAuthenticated}
      />
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { collectorId, pieceRefs, showCollectorError, queryCollectionError } = state.CollectionPage;

  const collectorMatches = getMarketplaceEntities(state, [{ type: 'user', id: collectorId }]);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    isAuthenticated: state.auth.isAuthenticated,
    collector: collectorMatches.length === 1 ? collectorMatches[0] : null,
    pieces: getMarketplaceEntities(state, pieceRefs),
    showCollectorError,
    queryCollectionError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onClearAskState: () => dispatch(clearAskState()),
});

const CollectionPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CollectionPageComponent);

export default CollectionPage;
